import React, { useEffect, useMemo, useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableActions from "./table-actions";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate, useSearchParams } from "react-router-dom";
import StatusChip from "src/shared/components/status-chip";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import { IClientType } from "src/types/clients";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { serviceLabels } from "src/constants/general";

const tableHeadTexts = [
  "Name in English",
  "Name in Arabic",
  "Client Name",
  "Location Type",
  "Valet",
  "Self Parking",
  "Actions",
];

const ActiveLocationsTable = ({
  rowsList,
  isLoading,
  cities,
  services,
  clients,
  refetch,
}: {
  rowsList: any;
  isLoading: boolean;
  cities: any[];
  services: any[];
  clients: any[];
  refetch: Function;
}) => {
  const navigate = useNavigate();
  const [auth] = useRecoilState(authState);
  const { permissions } = auth;
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize state from URL params
  const searchByParam = searchParams.get("search") ?? "";
  const serviceParam = searchParams.get("service") ?? "all";
  const pageParam = Number(searchParams.get("page")) || 0;
  const typeParam = searchParams.get("type") ?? "all";
  const clientIdParam = searchParams.get("clientId");
  const cityIdParam = searchParams.get("cityId");
  const initClient = clientIdParam ? { clientId: clientIdParam } : null;
  const initCity = cityIdParam ? { cityId: cityIdParam } : null;
  const [page, setPage] = useState(pageParam);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tempSearchBy, setTempSearchBy] = useState<string>(searchByParam);
  const [searchBy, setSearchBy] = useState<string>(searchByParam);
  const [selectedService, setSelectedService] = useState<string>(serviceParam);
  const [tempSelectedService, setTempSelectedService] =
    useState<string>(serviceParam);
  const [tempSelectedType, setTempSelectedType] = useState<string>(typeParam);
  const [selectedType, setSelectedType] = useState<string>(typeParam);
  const [selectedCity, setSelectedCity] = useState<any>(initCity);
  const [tempSelectedCity, setTempSelectedCity] = useState<any>(initCity);
  const [selectedClient, setSelectedClient] = useState<IClientType | null>(
    initClient as IClientType,
  );
  const [tempSelectedClient, setTempSelectedClient] =
    useState<IClientType | null>(initClient as IClientType);
  const prev = useRef({
    selectedType,
    selectedService,
    selectedClient,
    selectedCity,
    searchBy,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  //Filters
  const handleSelectServiceType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedService(value);
  };

  const handleSelectLocationType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedType(value);
  };

  const handleChangeClient = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedClient(newValue);
    setTempSelectedCity(undefined);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleApply = () => {
    setSelectedClient(tempSelectedClient);
    setSelectedCity(tempSelectedCity);
    setSelectedService(tempSelectedService);
    setSelectedType(tempSelectedType);
    setSearchBy(tempSearchBy);
  };

  const handleResetFilers = () => {
    setPage(0);
    setSelectedClient(null);
    setSelectedCity(undefined);
    setSelectedService("all");
    setSelectedType("all");
    setSearchBy("");

    setTempSelectedClient(null);
    setTempSelectedCity(undefined);
    setTempSelectedService("all");
    setTempSelectedType("all");
    setTempSearchBy("");
  };

  const handleSearchAndFilters = () => {
    let searchAndFiltersRows: any = [...rowsList];

    //Search by Arabic or English name
    if (searchBy.trim()) {
      const lowerCaseSearchBy = searchBy.toLowerCase();
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return (
          row.nameEn?.toLowerCase().includes(lowerCaseSearchBy) ||
          row.nameAr?.toLowerCase().includes(lowerCaseSearchBy)
          // || row.clientName?.toLowerCase().includes(lowerCaseSearchBy)
        );
      });
      searchAndFiltersRows = filteredRows;
    }

    //Filter by service
    if (selectedService === "valet") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.hasValetServices;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "selfParking") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.hasSelfParkingServices;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "golfCar") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.hasGolfCarServices;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "partialCarWash") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.hasPartialCarWashServices;
      });
      searchAndFiltersRows = filteredRows;
    } else if (selectedService === "fullCarWash") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.hasFullCarWashServices;
      });
      searchAndFiltersRows = filteredRows;
    }

    //Filter by location type
    if (selectedType !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.locationType === selectedType;
      });
      searchAndFiltersRows = filteredRows;
    }

    //Filter by city
    if (selectedCity) {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.cityId === selectedCity.cityId;
      });
      searchAndFiltersRows = filteredRows;
    }

    //Filter by client
    if (selectedClient) {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.clientId === selectedClient.clientId;
      });
      searchAndFiltersRows = filteredRows;
    }

    return searchAndFiltersRows;
  };

  const searchAndFiltersLocations = handleSearchAndFilters();

  const paginatedVisibleRows = useMemo(
    () =>
      searchAndFiltersLocations.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [
      page,
      rowsPerPage,
      rowsList,
      searchBy,
      selectedService,
      selectedClient,
      selectedCity,
      selectedType,
    ], // filters changes rerender
  );

  const emptyDataArr = searchAndFiltersLocations.length === 0 && !isLoading;

  const handleNavigate = () => {
    navigate("/locations/active/add");
  };

  const sortedClients = useMemo(() => {
    return (
      clients
        .slice()
        .sort((a, b) => a.clientName?.localeCompare(b.clientName)) || []
    );
  }, [clients]);

  const filteredLocations = useMemo(() => {
    return tempSelectedClient
      ? rowsList.filter(
          (loc: any) => loc.clientId === tempSelectedClient?.clientId,
        )
      : rowsList;
  }, [tempSelectedClient, rowsList]);

  const filteredCities = useMemo(() => {
    if (!cities) return [];

    const availableLocationsCitiesIds = filteredLocations.map(
      (loc: any) => loc.cityId,
    );

    return cities.filter((city) =>
      availableLocationsCitiesIds.includes(city.cityId),
    );
  }, [cities, filteredLocations, tempSelectedClient]);

  // Return to 1st page when filters change
  useEffect(() => {
    const changed =
      prev.current.selectedType !== selectedType ||
      prev.current.selectedService !== selectedService ||
      prev.current.selectedClient !== selectedClient ||
      prev.current.selectedCity !== selectedCity ||
      prev.current.searchBy !== searchBy;

    if (changed) {
      setPage(0);
    }

    prev.current = {
      selectedType,
      selectedService,
      selectedClient,
      selectedCity,
      searchBy,
    };
  }, [selectedType, selectedService, selectedCity, selectedClient, searchBy]);

  // Restore client filter
  useEffect(() => {
    const clientId = searchParams.get("clientId");
    if (clientId && sortedClients.length) {
      const found = sortedClients.find(
        (client) => client.clientId === clientId,
      );
      setSelectedClient(found);
      setTempSelectedClient(found);
    }
  }, [sortedClients]);

  // Restore city filter
  useEffect(() => {
    const cityId = searchParams.get("cityId");
    if (cityId && cities.length) {
      const found = cities.find((city) => city.cityId === cityId);
      setSelectedCity(found);
      setTempSelectedCity(found);
    }
  }, [cities]);

  // Sync URL when filters change
  useEffect(() => {
    setSearchParams({
      search: searchBy,
      type: selectedType,
      service: selectedService,
      page: page.toString(),
      clientId: selectedClient?.clientId ?? "",
      cityId: selectedCity?.cityId ?? "",
    });
  }, [
    searchBy,
    selectedClient,
    selectedCity,
    selectedService,
    selectedType,
    page,
  ]);

  return (
    <>
      {permissions?.locations.create ? (
        <Stack alignItems={"flex-end"} mb={2.2}>
          <Button
            data-testid="add-location-button"
            variant="contained"
            sx={{ gap: 0.5, ml: { xs: "0", lg: "auto" } }}
            onClick={handleNavigate}
          >
            <AddBoxIcon fontSize="small" sx={{ opacity: 0.7 }} />
            Add New Location
          </Button>
        </Stack>
      ) : null}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            data-testid="search-location-input"
            size="small"
            placeholder="Search"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* clients */}
          <CustomAutocomplete
            data-testid="filter-client-autocomplete"
            key={tempSelectedClient?.clientId ?? "client-autocomplete"}
            sx={{ width: { xs: "100%", sm: "200px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={sortedClients}
            getOptionLabel={(option) => option.clientName ?? ""}
            getOptionKey={(option) => `${option.clientId}`}
            value={tempSelectedClient}
            onChange={handleChangeClient}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Client"} />
            )}
          />
          {/* location type */}
          <FormControl size="small" sx={{ minWidth: "140px" }}>
            <InputLabel id="location-type-select-label">
              Location Type
            </InputLabel>
            <Select
              data-testid="filter-location-type-select"
              labelId="location-type-select-label"
              id="location-type-select"
              value={tempSelectedType}
              label="Location Type"
              onChange={handleSelectLocationType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"regular"}>Regular</MenuItem>
              <MenuItem value={"event"}>Event</MenuItem>
            </Select>
          </FormControl>
          {/* city */}
          <CustomAutocomplete
            data-testid="filter-city-autocomplete"
            key={tempSelectedCity?.cityId ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities || []}
            getOptionLabel={(option) => option.cityName ?? ""}
            getOptionKey={(option) => `${option.cityId}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
          <FormControl size="small" sx={{ minWidth: "140px" }}>
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              data-testid="filter-service-select"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleSelectServiceType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              {services.map((service: any) => (
                <MenuItem key={service} value={service}>
                  {serviceLabels[service] ?? service}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={"row"}>
          <Button
            onClick={handleResetFilers}
            data-testid="clear-filters-button"
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
            data-testid="apply-filters-button"
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          bgcolor: "#1A1A1A",
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: "10px 30px",
        }}
      >
        <Table
          data-testid="location-table"
          sx={{ minWidth: 650 }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              {tableHeadTexts.map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontWeight: 600,
                    color: "#A1A5B7",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={8} cellsLength={7} />
          ) : (
            <TableBody>
              {paginatedVisibleRows.map((row: any) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                  }}
                >
                  <TableCell>{row.nameEn}</TableCell>
                  <TableCell>{row.nameAr}</TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    {row.locationType}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      color={row.hasValetServices ? "success" : "warning"}
                      label={row.hasValetServices ? "ACTIVE" : "INACTIVE"}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={row.hasSelfParkingServices ? "ACTIVE" : "INACTIVE"}
                      color={row.hasSelfParkingServices ? "success" : "warning"}
                    />
                  </TableCell>
                  <TableCell>
                    <TableActions rowId={row.id} refetch={refetch} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>

        {emptyDataArr && <NoData />}

        <TablePagination
          data-testid="location-table-pagination"
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={searchAndFiltersLocations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};
export default ActiveLocationsTable;
